import { inject, Injectable } from "@angular/core";
import { OrderRequest, OrderIdRequest, AlgoOrderRequest, CancelAlgoOrderRequest } from "libs/okx-api/src";
import { forkJoin, from, Observable } from "rxjs";
import { parseFloatSafe } from "src/app/core/func";
import { OkxRestClient } from "src/app/core/market";
import { CreateOrderParams, CreateStopOrderParams, Order, OrderSide, OrderType, PositionSide } from "src/app/core/model/trade";
import { OrderHelper } from "../../order.helper";
import { chunk } from "lodash-es";

@Injectable({
  providedIn: 'root'
})
export class OkxOrderManager {

  private orderHelper = inject(OrderHelper)

  createOrder(rest: OkxRestClient, order: CreateOrderParams) {
    const params: OrderRequest = {
      instId: order.symbol,
      tdMode: 'cross', // OKX использует cross для фьючерсов
      clOrdId: this.orderHelper.generateClientOrderId(order.market, order.board),
      side: order.side == OrderSide.buy ? 'buy' : 'sell',
      ordType: order.type == OrderType.market ? 'market' : 'limit',
      sz: order.quantity.toString(),
    }
    if (order.price !== undefined) {
      params.px = order.price.toString()
    }
    if (order.positionSide !== undefined) {
      params.posSide = order.positionSide == PositionSide.both ? 'net' : order.positionSide == PositionSide.long ? 'long' : 'short'
    }
    if (order.closePosition === true) {
      params.reduceOnly = true
    }
    return from(rest.submitOrder(params))
  }

  createStopOrder(rest: OkxRestClient, order: CreateStopOrderParams) {
    const params: AlgoOrderRequest = {
      instId: order.symbol,
      tdMode: 'cross',
      algoClOrdId: this.orderHelper.generateClientOrderId(order.market, order.board),
      side: order.side == OrderSide.buy ? 'buy' : 'sell',
      ordType: 'conditional',
    }
    if (order.quantity !== undefined) {
      params.sz = parseFloatSafe(order.quantity).toString()
    } else {
      params.closeFraction = '1'
    }
    if (order.positionSide !== undefined) {
      params.posSide = order.positionSide == PositionSide.both ? undefined : order.positionSide == PositionSide.long ? 'long' : 'short'
    }
    if (order.closePosition === true) {
      params.cxlOnClosePos = true
      params.reduceOnly = true
    }

    // Для стоп-ордеров в OKX используется slTriggerPx и slOrdPx
    params.slTriggerPx = parseFloatSafe(order.triggerPrice).toString()
    params.slOrdPx = order.price !== undefined ? parseFloatSafe(order.price).toString() : '-1'

    return from(rest.placeAlgoOrder(params))
  }

  cancelOrder(rest: OkxRestClient, order: Order): Observable<unknown> {
    if (order.isStop) {
      const params: CancelAlgoOrderRequest = {
        algoId: order.orderId.toString(),
        instId: order.symbol
      }
      return from(rest.cancelAlgoOrder([params]))
    }

    const params: OrderIdRequest = {
      instId: order.symbol
    }
    if (order.orderId) {
      params.ordId = order.orderId.toString()
    } else if (order.clientOrderId) {
      params.clOrdId = order.clientOrderId
    }
    return from(rest.cancelOrder(params))
  }

  cancelOrders(rest: OkxRestClient, orders: Order[]) {
    // Разбиваем заявки по инструментам
    const ordersMap = new Map<string, string[]>()
    orders.forEach(order => {
      const arr = ordersMap.get(order.symbol) ?? []
      if (order.orderId) {
        arr.push(order.orderId.toString())
      } else if (order.clientOrderId) {
        arr.push(order.clientOrderId)
      }
      ordersMap.set(order.symbol, arr)
    })

    // Разбиваем на запросы по 20 заявок
    const requests: Observable<unknown>[] = []
    ordersMap.forEach((orderIds, symbol) => {
      requests.push(
        ...chunk(orderIds, 20).map(batch => {
          const params = batch.map(ordId => ({
            instId: symbol,
            ordId: ordId
          }))
          return from(rest.cancelMultipleOrders(params))
        })
      )
    })

    return forkJoin(requests)
  }

}
